/**
 * MLM API Service for Agent Portal
 * Communicates with the FastAPI /api/member/agent/* backend using the member JWT token.
 */

import { getAuthToken } from './memberApi';

const API_BASE_URL = (import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
const MEMBER_AGENT_BASE_URL = API_BASE_URL === '/api' || API_BASE_URL === ''
  ? '/api'
  : `${API_BASE_URL}/api`;

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}

async function apiFetch(path: string, options: RequestInit = {}): Promise<any> {
  const response = await fetch(`${MEMBER_AGENT_BASE_URL}/member/agent${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(options.headers || {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.detail || body.message || `Request failed: ${response.status}`);
  }

  return response.json();
}

export async function getDashboardData(): Promise<any> {
  return apiFetch('/dashboard');
}

export async function getTeamData(agentId?: string): Promise<any> {
  if (agentId) {
    return { level1: [], level2: [], totals: { level1_count: 0, level2_count: 0, level1_pv: 0 }, warning: 'Agent-id scoped team query is not supported in member agent API.' };
  }
  return apiFetch('/team');
}

export async function getCommissions(agentId?: string, period?: string): Promise<any> {
  const query = period ? `?period=${period}` : '';
  if (agentId) {
    return { transactions: [], totals: {}, balance: 0, warning: 'Agent-id scoped commissions query is not supported in member agent API.' };
  }
  return apiFetch(`/commissions${query}`);
}

export async function getRankProgress(agentId?: string): Promise<any> {
  if (agentId) {
    return { current: null, next: null, progress_pct: 0, warning: 'Agent-id scoped rank query is not supported in member agent API.' };
  }
  const dashboard = await getDashboardData();
  return dashboard.rank || { current: null, next: null, progress_pct: 0 };
}

export async function getUpcomingEvents(): Promise<any> {
  // Events not yet in backend — return empty list gracefully
  return { data: [] };
}

export async function registerForEvent(_eventId: number): Promise<any> {
  throw new Error('Event registration not yet available.');
}

export async function getReferralStats(): Promise<any> {
  const dashboard = await apiFetch('/dashboard');
  return dashboard.stats || {};
}

export default {
  getDashboardData,
  getTeamData,
  getCommissions,
  getRankProgress,
  getUpcomingEvents,
  registerForEvent,
  getReferralStats,
};
